import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { useRealtimeTick } from "@/lib/realtime";
import { Bell, X, Trophy, Zap, Target as TgIcon, Check, ExternalLink } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDate } from "@/lib/helpers";
import SaleDetailDialog from "@/components/SaleDetailDialog";

const iconFor = (kind) => {
  if (kind === "milestone") return <Trophy size={14} className="text-amber-300" />;
  if (kind === "success") return <Check size={14} className="text-emerald-300" />;
  if (kind === "warning") return <Zap size={14} className="text-amber-300" />;
  return <TgIcon size={14} className="text-blue-300" />;
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [saleId, setSaleId] = useState(null);
  const timer = useRef();

  const load = async () => {
    try {
      const { data } = await http.get("/notifications?limit=30");
      setItems(data.items);
      setUnread(data.unread);
    } catch {}
  };

  const tick = useRealtimeTick();
  useEffect(() => { if (tick) load(); }, [tick]); // eslint-disable-line
  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, []);

  const markRead = async () => {
    await http.post("/notifications/mark-read");
    load();
  };

  const remove = async (id) => {
    await http.delete(`/notifications/${id}`);
    load();
  };

  const openSale = (nid, sid) => {
    setSaleId(sid);
    setOpen(false);
  };

  return (
    <>
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v && unread > 0) markRead(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell"
          className="relative p-2 rounded-xl hover:bg-slate-800/80 border border-slate-800 bg-slate-900/60">
          <Bell size={18} className="text-slate-300" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 bg-slate-900 border-slate-800 p-0" align="end">
        <div className="p-3 border-b border-slate-800 flex items-center justify-between">
          <div className="text-sm font-semibold">Bildirimler</div>
          <span className="text-xs text-slate-500">{items.length} toplam</span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-xs">Henüz bildirim yok</div>
          ) : (
            items.map((n) => {
              const sid = n.meta?.sale_id;
              const clickable = !!sid;
              return (
                <div key={n.id}
                  onClick={() => clickable && openSale(n.id, sid)}
                  className={`p-3 border-b border-slate-800 flex gap-2 ${!n.read ? "bg-blue-500/5" : ""} ${clickable ? "cursor-pointer hover:bg-slate-800/60" : ""}`}
                  data-testid={`notification-${n.id}`}>
                  <div className="mt-0.5">{iconFor(n.kind)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-1">
                      {n.title}
                      {clickable && <ExternalLink size={10} className="text-blue-400" />}
                    </div>
                    <div className="text-xs text-slate-400">{n.body}</div>
                    <div className="text-[10px] text-slate-500 font-mono mt-1">{fmtDate(n.created_at)}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); remove(n.id); }} className="text-slate-500 hover:text-red-400">
                    <X size={14} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
    <SaleDetailDialog saleId={saleId} onClose={() => setSaleId(null)} />
    </>
  );
}
